/**
 * An intent's preposition bindings, validated: which role each phrase binds,
 * and which of them say where the direct object is (D261) — split out of
 * `compile-lexicon.ts` at the 500-line cap.
 *
 * @packageDocumentation
 */

import { COLON_TOKEN_ISSUE, isValidSingleWord, normalizeAuthoredToken, PREPOSITION_WORD_LIMIT } from './authored-token.js';
import { RESERVED_FUNCTION_WORDS } from './builtin-function-words.js';
import { type IntentDefinition, type PrepositionRole } from './lexicon-definition.js';

/** The parts of the compiler's accumulation state a binding writes to. */
export interface PrepositionCompilationState {
	readonly issues: string[];
	readonly prepositionTokens: Set<string>;
	readonly vocabularySet: Set<string>;
}

/** What an intent's bindings compile to. */
export interface CompiledPrepositionBindings {
	/** D261: the prepositions whose phrase says where the direct object is (`take … from`). */
	readonly objectLocatingPrepositions: ReadonlySet<string>;
	/** Preposition word → the role its phrase binds (declared, never guessed). */
	readonly prepositionRoles: ReadonlyMap<string, PrepositionRole>;
}

/**
 * Validate one intent's preposition bindings into its role map, and the
 * prepositions among them that locate the object (D261).
 *
 * @param intent - The authored intent
 * @param intentLocation - Author-facing locator for issue messages
 * @param state - Accumulation state (mutated: issues + vocabulary)
 * @returns The preposition → role map and the locating set (both empty when none/invalid)
 */
export const collectPrepositionBindings = (
	intent: IntentDefinition,
	intentLocation: string,
	state: PrepositionCompilationState,
): CompiledPrepositionBindings => {
	const prepositionRoles = new Map<string, PrepositionRole>();
	const objectLocatingPrepositions = new Set<string>();

	for (const binding of intent.prepositionBindings ?? []) {
		const prepositionText = normalizeAuthoredToken(binding.preposition);
		const bindingLocation = `${intentLocation} preposition "${prepositionText}"`;

		// D242: one word, or two read as one (`up to`) — never more, and every word a word.
		const prepositionWords = prepositionText.split(' ');

		if (prepositionWords.length > PREPOSITION_WORD_LIMIT || !prepositionWords.every(isValidSingleWord)) {
			state.issues.push(`${bindingLocation}: prepositions must be one non-blank word, or two (\`up to\`)`);
			continue;
		}

		if (prepositionWords.some((word) => RESERVED_FUNCTION_WORDS.has(word))) {
			state.issues.push(`${bindingLocation}: collides with a reserved function word`);
			continue;
		}

		if (prepositionText.includes(':')) {
			state.issues.push(`${bindingLocation}: ${COLON_TOKEN_ISSUE}`);
			continue;
		}

		// D229: a preposition that introduces the direct object is nothing on a
		// verb that forbids one — the declaration could never be read.
		if (binding.bindsAs === 'direct_object' && intent.objectPolicy === 'forbidden') {
			state.issues.push(`${bindingLocation}: introduces a direct object, and the intent forbids one`);
			continue;
		}

		// D261: only a phrase bound beside the object can say where the object is.
		if (binding.locatesObject === true && binding.bindsAs !== 'indirect_object') {
			state.issues.push(`${bindingLocation}: locates the object, and only an indirect_object can`);
			continue;
		}

		const existingRole = prepositionRoles.get(prepositionText);

		if (existingRole !== undefined && existingRole !== binding.bindsAs) {
			state.issues.push(
				`${bindingLocation}: bound as both "${existingRole}" and "${binding.bindsAs}" — one preposition, one role per intent`,
			);
			continue;
		}

		prepositionRoles.set(prepositionText, binding.bindsAs);

		if (binding.locatesObject === true) {
			objectLocatingPrepositions.add(prepositionText);
		}

		state.prepositionTokens.add(prepositionText);
		// The fuzzy pool's words are single words: a typed word is never corrected INTO a pair.
		prepositionWords.forEach((word) => state.vocabularySet.add(word));
	}

	return { objectLocatingPrepositions, prepositionRoles };
};
